/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Slices
import {
	createMainSlice,
	IMainSlice,
} from '@features/main/slices/createMainSlice';

// Slices
import {
	createDashboardSlice,
	IDashboardSlice,
} from '@features/dashboard/slices/createDashboardSlice';

import {
	createProductionsSlice,
	IProductionSlice,
} from '@features/sourcing/productions/slices/createProductionsSlice';

import { createProductionStartMintSlice } from '@features/sourcing/productions/components/StartMint/slices/createProductionsStartMintSlice';

import {
	createMintHistorySlice,
	IMintHistorySlice,
} from '@features/sourcing/mint-history/slices/createMintHistorySlice';

import {
	createApiTokensSlice,
	IApiTokenSlice,
} from '@features/resources/api-tokens/slices/createApiTokensSliceV2';

import {
	createAuthSlice,
	IAuthSlice,
} from '@features/auth/slices/createAuthSlice';

import {
	createConversionFactorsSlice,
	IConversionFactorSlice,
} from '@features/resources/conversion-factors/slice/createConversionFactorSlice';

import { IProductionStartMintSlice } from '@features/sourcing/productions/components/StartMint/slices/createProductionsStartMintSlice';

import {
	createMeasurabilityProjectsSlice,
	IMeasurabilityProjectSlice,
} from '@features/catalogs/meausrability-projects/list/slice/createMeasurabilityProjectSlice';

import {
	createAdditionalityProjectsSlice,
	IAdditionalityProjectSlice,
} from '@features/catalogs/additionality-projects/list/slice/createAdditionalityProjectSlice';

import {
	createMeterPointsSlice,
	IMeterPointSlice,
} from '@features/resources/meter-points/slice/createMeterPointSlice';

import {
	createSitesSlice,
	ISiteSlice,
} from '@features/resources/sites/slice/createSitesSlice';

import {
	createOffsetsDatatableSlice,
	IOffsetsDatatableSlice,
} from '@features/offsets/slices/createOffsetsDatatableSlice';

import {
	createImportHistorySlice,
	IImportHistorySlice,
} from '@features/sourcing/import-history/slices/createImportHistorySlice';

import {
	createProductionsDatatableSlice,
	IProductionsDatatableSlice,
} from '@features/sourcing/productions/slices/createProductionsDatatableSlice';

import {
	createApiTokensDatatableSlice,
	IApiTokensDatatableSlice,
} from '@features/resources/api-tokens/slices/createApiTokensDatatableSlice';

import {
	createSitesDatatableSlice,
	ISitesDatatableSlice,
} from '@features/resources/sites/slice/createSitesDatatableSlice';

import {
	createMeterpointsDatatableSlice,
	IMeterpointsDatatableSlice,
} from '@features/resources/meter-points/slice/createMeterPointDatatableSlice';

import {
	createConversionFactorsDatatableSlice,
	IConversionFactorsDatatableSlice,
} from '@features/resources/conversion-factors/slice/createConversionFactorDatatableSlice';

import {
	createMeasurabilitiesSlice,
	IMeasurabilitiesSlice,
} from '@features/resources/measurabilities/slices/createMeasurabilitieeSlice';

import {
	createMeasurabilitiesDatatableSlice,
	IMeasurabilitiesDatatableSlice,
} from '@features/resources/measurabilities/slices/createMeasurabilitiesDatatableSlice';

import {
	createAdditionalitiesDatatableSlice,
	IAdditionalitiesDatatableSlice,
} from '@features/resources/additionalities/slices/createAdditionalitiesDatatableSlice';

import {
	createAdditionalitiesSlice,
	IAdditionalitiesSlice,
} from '@features/resources/additionalities/slices/createAdditionalitiesSlice';

import {
	createOffsetSlice,
	IOffsetSlice,
} from '@features/offsets/slices/createOffsetsSlice';

import {
	createProducersSlice,
	IProducerSlice,
} from '@features/resources/producers/slices/createProducersSlice';

import {
	createProducersDatatableSlice,
	IProducersDatatableSlice,
} from '@features/resources/producers/slices/createProducersDatatableSlice';

import {
	createBrandsSlice,
	IBrandSlice,
} from '@features/resources/brands/slices/createBrandsSlice';

import {
	createBrandsDatatableSlice,
	IBrandsDatatableSlice,
} from '@features/resources/brands/slices/createBrandsDatatableSlice';

import {
	createOrganizationsSlice,
	IOrganizationSlice,
} from '@features/resources/organizations/slices/createOrganizationsSlice';

import {
	createOrganizationsDatatableSlice,
	IOrganizationsDatatableSlice,
} from '@features/resources/organizations/slices/createOrganizationsDatatableSlice';

import {
	createUsersSlice,
	IUserSlice,
} from '@features/resources/users/slices/createUsersSlice';

import {
	createOffsetAddSlice,
	IOffsetAddSlice,
} from '@features/offsets/slices/createOffsetAddSlice';

import {
	createUsersDatatableSlice,
	IUsersDatatableSlice,
} from '@features/resources/users/slices/createUsersDatatableSlice';

import {
	createMintProductionsSlice,
	IMintProductionSlice,
} from '@features/sourcing/productions/slices/createMintProductionsSlice';

import {
	createAddProductionSlice,
	IAddProductionSlice,
} from '@features/sourcing/productions/slices/createAddProductionSlice';

interface MainStore {}

export type TStore = MainStore &
	IConversionFactorSlice &
	IProductionStartMintSlice &
	IMeasurabilityProjectSlice &
	IAdditionalityProjectSlice &
	IProductionSlice &
	ISiteSlice &
	IMeterPointSlice &
	IImportHistorySlice &
	IOffsetsDatatableSlice &
	IProductionsDatatableSlice &
	IApiTokensDatatableSlice &
	ISitesDatatableSlice &
	IApiTokenSlice &
	IMeterpointsDatatableSlice &
	IConversionFactorsDatatableSlice &
	IMeasurabilitiesSlice &
	IMeasurabilitiesDatatableSlice &
	IAdditionalitiesSlice &
	IAdditionalitiesDatatableSlice &
	IMintHistorySlice &
	IMainSlice &
	IAuthSlice &
	IOffsetSlice &
	IProducerSlice &
	IProducersDatatableSlice &
	IBrandSlice &
	IBrandsDatatableSlice &
	IOrganizationSlice &
	IOrganizationsDatatableSlice &
	IUserSlice &
	IUsersDatatableSlice &
	IDashboardSlice &
	IOffsetAddSlice &
	IMintProductionSlice &
	IAddProductionSlice;

/* -------------------------------------------------------------------------- */
/*                                Global state                                */
/* -------------------------------------------------------------------------- */
export const useStore = create<TStore>()(
	persist(
		(set, get, storeApi) => ({
			...createOffsetAddSlice(set, get, storeApi),
			...createDashboardSlice(set, get, storeApi),
			...createOffsetSlice(set, get, storeApi),
			...createAuthSlice(set, get, storeApi),
			...createMainSlice(set, get, storeApi),
			...createMintHistorySlice(set, get, storeApi),
			...createProductionStartMintSlice(
				set,
				get,
				storeApi
			),
			...createSitesSlice(set, get, storeApi),
			...createProductionsSlice(set, get, storeApi),
			...createApiTokensSlice(set, get, storeApi),
			...createConversionFactorsSlice(set, get, storeApi),
			...createMeasurabilityProjectsSlice(
				set,
				get,
				storeApi
			),
			...createAdditionalityProjectsSlice(
				set,
				get,
				storeApi
			),
			...createMeterPointsSlice(set, get, storeApi),
			...createImportHistorySlice(set, get, storeApi),
			...createOffsetsDatatableSlice(set, get, storeApi),
			...createProductionsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createApiTokensDatatableSlice(
				set,
				get,
				storeApi
			),
			...createSitesDatatableSlice(set, get, storeApi),
			...createMeasurabilitiesSlice(set, get, storeApi),
			...createAdditionalitiesSlice(set, get, storeApi),
			...createMeterpointsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createConversionFactorsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createMeasurabilitiesDatatableSlice(
				set,
				get,
				storeApi
			),
			...createAdditionalitiesDatatableSlice(
				set,
				get,
				storeApi
			),
			...createProducersDatatableSlice(
				set,
				get,
				storeApi
			),
			...createProducersSlice(set, get, storeApi),
			...createBrandsSlice(set, get, storeApi),
			...createBrandsDatatableSlice(set, get, storeApi),
			...createOrganizationsSlice(set, get, storeApi),
			...createOrganizationsDatatableSlice(
				set,
				get,
				storeApi
			),
			...createUsersSlice(set, get, storeApi),
			...createUsersDatatableSlice(set, get, storeApi),
			...createMintProductionsSlice(set, get, storeApi),
			...createAddProductionSlice(set, get, storeApi),
		}),
		{
			name: 'IGLU-store',
		}
	)
);

export default useStore;
